import clsx from "clsx";
import deburr from "lodash/deburr";
import groupBy from "lodash/groupBy";
import React, { useEffect, useRef, useState } from "react";
import CheckboxFilterItem from "web/react/components/checkbox-filter-item/checkbox-filter-item";
import { useFilterContext } from "web/react/components/filters-emo/filter-container-emo";
import { Search } from "web/react/emo/search";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";
import { FilteredScrollAreaFilterOptionGroup } from "web/types/universal-filters";
import { FilterAlphabetisedSection } from "./filter-alphabetised-section";
import styles from "./filter-option-group.module.css";

interface FilterOptionGroupProps {
    isVisible: boolean;
    optionGroup: FilteredScrollAreaFilterOptionGroup;
    lazyFacetFetch: (optionGroupId: string) => void;
    lazyFacetIsLoading: boolean;
    showSearch?: boolean;
    title?: string;
}

// Utility for getting the first char from string
const REGEX_AZ = /([A-Z])/i;
const REGEX_NUMBER = /([0-9])/;

function getFirstCharacter({ label }: { label: string }): string {
    const firstCharacter = label.charAt(0).normalize("NFD")[0].toUpperCase();

    if (REGEX_AZ.test(firstCharacter)) {
        return firstCharacter;
    } else if (REGEX_NUMBER.test(firstCharacter)) {
        return "0-9";
    }

    return "!#";
}

export function FilterOptionGroup({
    isVisible,
    optionGroup,
    lazyFacetFetch,
    showSearch = true,
    title,
}: FilterOptionGroupProps): JSX.Element {
    const { isMobileViewport } = useDomViewport();
    const { onFilterItemClick, pendingFilterInfo } = useFilterContext();
    const [query, setQuery] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    const shouldLoadOptions = optionGroup.lazy_loaded && isVisible;
    const toRender = optionGroup.options.filter(
        (option) => query === "" || deburr(option.label.toLowerCase()).includes(deburr(query))
    );
    const groupedBy = Object.entries(groupBy(optionGroup.options, getFirstCharacter));
    const { id, search_placeholder: placeholder } = optionGroup;

    useEffect(() => {
        if (shouldLoadOptions) {
            // rothko caching ensures we don't duplicate calls, e.g. if a filter is
            // added and then removed again
            lazyFacetFetch(optionGroup.id);
        }
    }, [lazyFacetFetch, shouldLoadOptions, optionGroup.id]);

    function handleFilter(event: React.ChangeEvent<HTMLInputElement>): void {
        setQuery(event.target.value.toLowerCase());
    }

    function onBlurSearchInput(): void {
        analytics.event("filter-search", "blur", title);
    }

    function onFocusSearchInput(): void {
        analytics.event("filter-search", "focus", title);

        // On iOS, the keyboard hides the autocomplete results.
        // As a fix, on search input focus, we are scrolling the search input
        // to the very top of the available Drawer scrolling space.
        if (isMobileViewport) {
            ref?.current?.scrollIntoView({
                behavior: "smooth",
            });
        }
    }

    function renderSearch(): JSX.Element {
        return (
            <View
                paddingTop={{ sm: "none", md: "none", lg: "md" }}
                paddingBottom={{ sm: "md", md: "md", lg: "sm" }}
                className={styles.searchContainer}
            >
                <Search
                    ariaControls={`search-result_${id}`}
                    placeholder={placeholder}
                    onChange={handleFilter}
                    onBlur={onBlurSearchInput}
                    onFocus={onFocusSearchInput}
                />
            </View>
        );
    }

    return (
        <div className={styles.background} ref={ref}>
            {showSearch && renderSearch()}
            <ol
                className={clsx(styles.results, {
                    [styles.hideSearch]: !showSearch,
                })}
                role="region"
                id={`search-result_${id}`}
                aria-live="polite"
                aria-atomic="false"
            >
                {query ? (
                    <>
                        {toRender.map((item) => (
                            <li id={`${id}-${item.value}`} key={`${id}-${item.value}`}>
                                <CheckboxFilterItem
                                    query={query}
                                    {...{ item, onClick: onFilterItemClick, pendingFilterInfo }}
                                    isSmallItem={true}
                                />
                            </li>
                        ))}
                    </>
                ) : (
                    <>
                        {groupedBy.map(([firstLetter, options]) => (
                            <FilterAlphabetisedSection key={firstLetter} title={firstLetter}>
                                {options.map((item) => (
                                    <li
                                        key={item.label}
                                        id={`${id}-${item.value}`}
                                        className={clsx(styles.listItem, {
                                            [styles.listItemOneColumn]: !showSearch,
                                        })}
                                    >
                                        <CheckboxFilterItem
                                            query={query}
                                            {...{
                                                item,
                                                onClick: onFilterItemClick,
                                                pendingFilterInfo,
                                            }}
                                            isSmallItem={true}
                                        />
                                    </li>
                                ))}
                            </FilterAlphabetisedSection>
                        ))}
                    </>
                )}
            </ol>
        </div>
    );
}
